/* eslint-disable @typescript-eslint/no-empty-function */
import { Dialog } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { IamData } from '../../services/user-service';
import { useState } from 'react';
import { EditIamProfileDetails } from 'features/profile/component/modals/edit-iam-profile-details/edit-iam-profile-details';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { ROLES } from 'constant/roles';

/**
 * Displays detailed information about a user with options to reset their password, resend the activation link,
 * or edit their profile details.
 *
 * Features:
 * - Displays the user's email, phone number, roles, join date, last login time, status, and MFA settings.
 * - Allows the user to reset the password (if active) or resend the activation link (if inactive).
 * - Provides a button to edit the user's profile.
 *
 * @param {ExpandedUserDetailsProps} props - The props for the expanded user details component.
 * @param {IamData} props.user - The user object containing the detailed information to display.
 * @param {(user: IamData) => void} props.onResetPassword - A callback function to handle the reset password action.
 * @param {(user: IamData) => void} props.onResendActivation - A callback function to handle the resend activation link action.
 *
 * @returns {JSX.Element} - The rendered expanded user details component.
 *
 * @example
 * <ExpandedUserDetails
 *   user={selectedUser}
 *   onResetPassword={handleResetPassword}
 *   onResendActivation={handleResendActivation}
 * />
 */

interface ExpandedUserDetailsProps {
  user: IamData;
  onResetPassword: (user: IamData) => void;
  onResendActivation: (user: IamData) => void;
  onDeactivateUser?: (user: IamData) => void;
  onChangePlan?: (user: IamData) => void;
  onCancelSubscription?: (user: IamData) => void;
}

const ExpandedUserDetails: React.FC<ExpandedUserDetailsProps> = ({
  user,
  onResetPassword,
  onResendActivation,
  onDeactivateUser,
  onChangePlan,
  onCancelSubscription
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const currentUser = useCommonStore((state) => state.user);
  const isSuperAdmin = useCommonStore((state) => state.isSuperAdmin);

  // Check if the current user is trying to deactivate themselves
  const isCurrentUser = currentUser?.id === user.itemId;

  // Check if the user has only org_user role
  const hasOnlyOrgUserRole = user.roles?.includes(ROLES.ORG_USER) && !user.roles.includes(ROLES.ORG_ADMIN);

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };
  const formatLastLoginTime = (lastLoggedInTime: string | Date | null | undefined) => {
    if (!lastLoggedInTime) {
      return '-';
    }

    const date = new Date(lastLoggedInTime);

    if (date.getFullYear() === 1) {
      return '-';
    }

    try {
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return '-';
    }
  };
  return (
    <div className="p-4 space-y-4">
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-medium text-medium-emphasis">Email</h3>
          <p className="text-sm text-high-emphasis">{user.email}</p>
        </div>

        <div className="flex justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-medium text-medium-emphasis">Mobile no.</h3>
            <p className="text-sm text-high-emphasis">{user.phoneNumber ?? '-'}</p>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-medium-emphasis">Role(s)</h3>
            <p className="text-sm text-high-emphasis first-letter:uppercase">
              {user.roles && user.roles.length > 0 ? user.roles.join(', ') : '-'}
            </p>
          </div>
        </div>

        <div className="flex justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-medium text-medium-emphasis">Joined on</h3>
            <p className="text-sm text-high-emphasis">
              {new Date(user.createdDate).toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-medium-emphasis">Last log in</h3>
            <div className="text-sm text-high-emphasis">
              {user.lastLoggedInTime && new Date(user.lastLoggedInTime).getFullYear() !== 1 ? (
                formatLastLoginTime(user.lastLoggedInTime)
              ) : (
                <div className="text-muted-foreground">-</div>
              )}
            </div>
          </div>
        </div>

        <div className="flex space-x-4">
          <div className="flex-1">
            <h3 className="text-sm  text-medium-emphasis">Status</h3>
            <h3 className={user.active ? 'text-success ' : 'text-error'}>
              {user.active ? 'Active' : 'Deactivated'}
            </h3>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-medium-emphasis">MFA</h3>
            <p className="text-sm text-high-emphasis">{user.mfaEnabled ? 'Enabled' : 'Disabled'}</p>
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col gap-2">
        <Button size="sm" className="w-full" onClick={handleEditClick}>
          Edit
        </Button>
        <div className="flex w-full flex-row gap-4">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => user?.active ? onResetPassword(user) : onResendActivation(user)}
          >
            {user?.active ? 'Reset Password' : 'Resend Activation Link'}
          </Button>
          {user?.active && onDeactivateUser && !isCurrentUser && (
            <Button
              variant="outline"
              size="sm"
              className="w-full text-error hover:text-error"
              onClick={() => onDeactivateUser(user)}
            >
              Deactivate User
            </Button>
          )}
        </div>
        {onChangePlan && (
          <Button
            variant="outline"
            size="sm"
            className={`w-full ${hasOnlyOrgUserRole ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={hasOnlyOrgUserRole ? undefined : () => onChangePlan(user)}
            disabled={hasOnlyOrgUserRole}
            title={hasOnlyOrgUserRole ? 'This action is not available for org_user role' : ''}
          >
            Change Plan for User
          </Button>
        )}
        {onCancelSubscription && isSuperAdmin && (
          <Button
            variant="outline"
            size="sm"
            className={`w-full ${hasOnlyOrgUserRole ? 'text-error opacity-50 cursor-not-allowed' : 'text-error hover:text-error'}`}
            onClick={hasOnlyOrgUserRole ? undefined : () => onCancelSubscription(user)}
            disabled={hasOnlyOrgUserRole}
            title={hasOnlyOrgUserRole ? 'This action is not available for org_user role' : ''}
          >
            Cancel Subscription
          </Button>
        )}
      </div>

      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        {user && isEditModalOpen && <EditIamProfileDetails userInfo={user} onClose={handleCloseEditModal} />}
      </Dialog>
    </div>
  );
};

export default ExpandedUserDetails;
